export const COLORS = {
    DA: "#2e8b57",    // Greenish
    AR: "#a0522d",    // Brown/Orange
    EV: "#663399",    // Purple
    NCF: "#b22222",   // Red
    CORE: "#ffffff",  // White
    TEXT: "#ffffff",
    BG: "#f0f2f5"
};

export const ncrfData = {
    "ANF": {
        title: "ANF CORE",
        subtitle: "Autonomous Function",
        color: COLORS.CORE,
        position: [0, 0, 0],
        content: `
          <h2 class="text-3xl font-bold text-gray-800 mb-4">Autonomous Network Function</h2>
          <p class="text-lg text-gray-600 mb-6 leading-relaxed">The central entity (e.g., Energy Saving AI) being controlled. It operates stochastically, meaning its decisions are probabilistic.</p>
          <div class="bg-blue-50 p-6 rounded-xl border-l-4 border-blue-500 shadow-sm">
              <h4 class="font-bold text-blue-700 text-lg mb-2">Key Risks:</h4>
              <ul class="list-disc pl-5 text-blue-800 space-y-1">
                  <li>Malicious Proposals (False Positives)</li>
                  <li>Biased Decision Making</li>
                  <li>Black-box unpredictability</li>
              </ul>
          </div>
      `
    },
    "DA": {
        title: "NRCF-DA",
        subtitle: "DATA ANALYZER",
        color: COLORS.DA,
        position: [-4.2, 2, 0],
        content: `
          <h2 class="text-3xl font-bold text-green-700 mb-4">Data Analyzer</h2>
          <p class="text-lg text-gray-600 mb-6 leading-relaxed">Ensures "Data Integrity" before it reaches the AI. It detects skewed or fraudulent data inputs.</p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="bg-white p-4 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <span class="font-bold text-green-600 block text-lg mb-1">Benford's Law</span>
                  <span class="text-sm text-gray-500">Checks naturally occurring number distribution to spot anomalies.</span>
              </div>
              <div class="bg-white p-4 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <span class="font-bold text-green-600 block text-lg mb-1">Entropy Analysis</span>
                  <span class="text-sm text-gray-500">Measures data uncertainty and randomness.</span>
              </div>
          </div>
      `
    },
    "AR": {
        title: "NRCF-AR",
        subtitle: "ALGORITHM RESULT EVALUATOR",
        color: COLORS.AR,
        position: [4.2, 2, 0],
        content: `
          <h2 class="text-3xl font-bold text-amber-800 mb-4">Algorithm Result Evaluator</h2>
          <p class="text-lg text-gray-600 mb-6 leading-relaxed">The "Safety Valve" that checks AI outputs before execution.</p>
          <h4 class="font-bold text-gray-700 mb-3 text-lg">Detection Models:</h4>
          <ul class="space-y-3">
              <li class="flex items-start bg-amber-50 p-3 rounded-lg">
                  <span class="bg-amber-100 text-amber-800 text-xs font-bold px-2 py-1 rounded mr-3 mt-0.5">ANOBIA</span>
                  <span class="text-gray-700">Ensemble anomaly detection (Isolation Forest + LOF) to catch outliers.</span>
              </li>
              <li class="flex items-start bg-amber-50 p-3 rounded-lg">
                  <span class="bg-amber-100 text-amber-800 text-xs font-bold px-2 py-1 rounded mr-3 mt-0.5">INFEROBIA</span>
                  <span class="text-gray-700">Uses Decision Trees to explain <em>why</em> a decision is bad (Explainable AI).</span>
              </li>
          </ul>
      `
    },
    "EV": {
        title: "NRCF-EV",
        subtitle: "EVALUATOR SCALE",
        color: COLORS.EV,
        position: [-4.2, -2, 0],
        content: `
          <h2 class="text-3xl font-bold text-purple-800 mb-4">Evaluator Scale</h2>
          <p class="text-lg text-gray-600 mb-6 leading-relaxed">Quantifies the maturity of the AI using medical-grade scoring systems.</p>
          <div class="grid grid-cols-1 gap-3">
              <div class="border-l-4 border-purple-500 pl-4 py-2 bg-purple-50/50 rounded-r-lg">
                  <strong class="block text-purple-900 text-lg">GANFS</strong>
                  <span class="text-sm text-gray-600">Glasgow Coma Scale adapted for Network Autonomy.</span>
              </div>
              <div class="border-l-4 border-purple-500 pl-4 py-2 bg-purple-50/50 rounded-r-lg">
                  <strong class="block text-purple-900 text-lg">MEGABITS</strong>
                  <span class="text-sm text-gray-600">Apgar-style rapid health check for algorithms.</span>
              </div>
              <div class="border-l-4 border-purple-500 pl-4 py-2 bg-purple-50/50 rounded-r-lg">
                  <strong class="block text-purple-900 text-lg">Gleason Score</strong>
                  <span class="text-sm text-gray-600">Histology-inspired pattern grading.</span>
              </div>
          </div>
      `
    },
    "NCF": {
        title: "NRCF-NCF",
        subtitle: "NORMATIVE CONTROL FUNCTION",
        color: COLORS.NCF,
        position: [4.2, -2, 0],
        content: `
          <h2 class="text-3xl font-bold text-red-800 mb-4">Normative Control Function</h2>
          <p class="text-lg text-gray-600 mb-6 leading-relaxed">The "Intelligent Plane" that enforces rules and sequences actions across the network.</p>
          
          <div class="bg-red-50 p-6 rounded-xl border border-red-100 space-y-6">
              <div>
                  <h4 class="font-bold text-red-800 mb-2 text-lg flex items-center gap-2">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
                      Multi-ANF Orchestration
                  </h4>
                  <p class="text-red-700 text-sm leading-relaxed">
                      Unlike other components that monitor a single ANF Core, <strong>NRCF-NCF</strong> orchestrates <em>multiple</em> Autonomous Functions simultaneously. It prevents conflicts (e.g., Energy Saving vs. Load Balancing) through the <strong>ASANA</strong> sequencing logic.
                  </p>
              </div>

              <div class="grid grid-cols-2 gap-4">
                  <div class="bg-white/60 p-3 rounded-lg border border-red-200">
                      <strong class="block text-red-900 text-sm mb-1">ASANA Framework</strong>
                      <span class="text-xs text-red-800">Sequences actions to avoid collisions.</span>
                  </div>
                  <div class="bg-white/60 p-3 rounded-lg border border-red-200">
                      <strong class="block text-red-900 text-sm mb-1">What-If Scenarios</strong>
                      <span class="text-xs text-red-800">Simulates outcomes before execution.</span>
                  </div>
              </div>
          </div>
      `
    }
};
